import { HotelCalendarType } from "@website/accommodation-ui/hotel-calendar";
import { HotelFaqPropertiesType } from "@website/accommodation-ui/hotel-faq";
import { HotelInboundLinksPropertiesType } from "@website/accommodation-ui/hotel-inbound-links";
import { HotelReserveBookingCardType } from "@website/accommodation-ui/hotel-reserve-booking-card";
import { HotelReserveDetailsPropertiesTypes } from "@website/accommodation-ui/hotel-reserve-details";
import { HotelSeoContentPropertiesType } from "@website/accommodation-ui/hotel-seo-content";
import {
  CancellationPlanType,
  CancellationRulesImportance,
  villaReserveCancellationPolicyType
} from "@website/accommodation-ui/villa-reserve-cancellation-policies";
import { useFetcher } from "@website/fetcher/use-fetcher";
import { useLoginStatus } from "@website/hooks/auth-manager";
import { MaterialNameType } from "@website/icons/material";
import {
  BreadcrumbItemType,
  BreadcrumbPropertiesType
} from "@website/ui/breadcrumb";
import { useSnackbar } from "@website/ui/snackbar";
import { getOrigin } from "@website/utils/channel/get-origin";
import {
  daysBetweenDates,
  isInvalidCheckin,
  today,
  tomorrow,
  transformDate
} from "@website/utils/date-format";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import { ReserveFacilitiesPropertiesTypes } from "@website/widget/reserve-facilities";
import { ReserveMapPropertiesTypes } from "@website/widget/reserve-map";
import { ReservePoliciesPropertiesTypes } from "@website/widget/reserve-policies";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import omit from "lodash-es/omit";
import pick from "lodash-es/pick";
import { useRouter } from "next/router";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ENDPOINTS } from "../../../constants";
import HamraheAval from "./assets/MCI.png";
import IrancellLogo from "./assets/irancel.png";
import Raitel from "./assets/rightel.png";
import { useBooking } from "./booking.hook";
import { useCalendar } from "./calendar.hook";
import { useHotelComments } from "./comments.hook";
import {
  fetchCalendarData,
  fetchInstalmentWallet,
  fetchPropertyPrice,
  fetchRoomInfo,
  fetchUsefulnessReviewData
} from "./index.server";
import {
  handleFacilicyModalScrollChanged,
  useRoomPageTracking
} from "./index.tracking";
import {
  HotelCalendarPropertiesType,
  HotelPagePropertiesType,
  RoomPropertyResponseType
} from "./index.types";

export const useHotelPageDataTransform = ({
  roomInfo,
  seoData,
  hotelRateData
}: HotelPagePropertiesType) => {
  const router = useRouter();
  const { enqueue } = useSnackbar();

  const { isLoggedIn } = useLoginStatus();

  const {
    setQueryParameters,
    onCalendarChange,
    calendarDate,
    calendarInitialShowDates
  } = useCalendar();
  const passengersCount = Number(get(router.query, "room_capacity") ?? 0);
  const { data: freshRoomInfo } = useFetcher({
    enabled: Boolean(router.query["roomId"]),
    queryKey: ["roomInfo", router.query.roomId],
    placeholderData: roomInfo,
    queryFn: () => fetchRoomInfo(router.query)
  });
  const maximumAllowedPassengerCount =
    (freshRoomInfo?.extra_capacity || 0) + (freshRoomInfo?.capacity || 0);
  const isPassengerLimitExceeded =
    passengersCount > maximumAllowedPassengerCount;

  const { data: roomProperty, isFetching } = useFetcher({
    enabled: Boolean(router.query["roomId"]),
    queryKey: [
      "roomsProperty",
      JSON.stringify(router.query),
      router?.query?.["date_from"],
      router?.query?.["date_to"]
    ],
    queryFn: () =>
      fetchPropertyPrice(router.query).then(
        (data) => data as RoomPropertyResponseType
      )
  });
  const { handleTrackReserveButtonClicked } = useRoomPageTracking(
    freshRoomInfo,
    roomProperty
  );
  const [reviewIds, setReviewIds] = useState([]);
  const [hasError, setHasError] = useState(false);
  const onUsefulnessClick = (id: number) =>
    setReviewIds((previousState) => [
      ...previousState,
      { id, isFetched: isFetched && !hasError }
    ]);

  const { isFetched } = useFetcher({
    enabled: reviewIds.length > 0 && isLoggedIn,
    queryKey: ["usefulnessReview", reviewIds?.[reviewIds?.length - 1]?.id],
    queryFn: ({ queryKey }) =>
      fetchUsefulnessReviewData(Number(queryKey[1]))
        .then(() => setHasError(false))
        .catch((error) => {
          setHasError(true);
          enqueue(error?.response?.message, "error", "one-line");
        })
  });

  const { data: calendarAvailabilityData } =
    useFetcher<HotelCalendarPropertiesType>({
      queryFn: () => fetchCalendarData(router.query.roomId),
      enabled: Boolean(router.query.roomId),
      queryKey: ["calendarData", router.query.roomId]
    });

  const { comments, onLoadMore, onLoadFilter, filters, isLoading } =
    useHotelComments({
      hotelId: freshRoomInfo?.property?.id,
      roomId: freshRoomInfo?.id
    });

  const hotelReserveRateProperties = {
    ...hotelRateData,
    size: "medium" as const,
    hotelType: "اقامتگاه"
  };

  const { isLoading: requestBookingLoading, ...booking } = useBooking({
    propertyID: freshRoomInfo?.property?.id,
    min_stay: roomProperty?.min_stay,
    handleTrackReserveButtonClicked
  });

  const extraCapacity = Number(router.query.extra_capacity);

  const onChangePassengersCount = useCallback(
    (value: number) => {
      const extra_capacity =
        value > freshRoomInfo?.capacity ? value - freshRoomInfo?.capacity : 0;

      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            room_capacity: value,
            extra_capacity
          }
        },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [router, freshRoomInfo]
  );

  const disableDates = useMemo(
    () =>
      Object?.entries(calendarAvailabilityData?.calender || [])
        .filter(
          (value) =>
            value[1]?.availability === 0 ||
            !value[1]?.price ||
            value[1].price === 0
        )
        .map(([date]) => date),
    [calendarAvailabilityData?.calender]
  );

  useEffect(() => {
    const isDisabledDateSelected = disableDates.some(
      (d) =>
        d >= router.query.date_from &&
        d <= router.query.date_to &&
        d !== router.query.date_to
    );

    if (
      isDisabledDateSelected ||
      isInvalidCheckin(router.query.date_from, router.query.date_to)
    ) {
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, date_from: today(), date_to: tomorrow() }
        },
        undefined,
        { shallow: true, scroll: false }
      );
    }
  }, [router, disableDates]);

  const lastDate = useMemo(
    () =>
      Object?.entries(calendarAvailabilityData?.calender || [])?.at(-1)?.[0],
    [calendarAvailabilityData?.calender]
  );

  const disableLimit = useMemo(
    () => ({
      disableAfterDate: lastDate
        ? transformDate(new Date(lastDate as unknown as string))
        : undefined
    }),
    [lastDate]
  );

  const { data: instalmentWallet } = useFetcher({
    enabled: isLoggedIn,
    retry: false,
    queryKey: ["instalmentWallet"],
    queryFn: () => fetchInstalmentWallet()
  });

  const snappPayData = useMemo(
    () => ({
      isEligible: instalmentWallet?.isEligible,
      showDownPayment: instalmentWallet?.showDownPayment,
      markup: instalmentWallet?.markup
    }),
    [instalmentWallet]
  );

  const hotelBreadcrumbsProperties = useMemo(
    () =>
      ({
        color: "default" as const,
        variant: "default" as const,
        list: [
          {
            title: "ویلا و اقامتگاه",
            href: `${getOrigin()}/villa`
          },
          {
            title: `ویلاهای استان ${freshRoomInfo?.property?.state?.title}`,
            href: {
              pathname: "/[locationType]/[cityTitle]",
              query: {
                ...pick(router.query, ["cityTitle", "locationType"]),
                locationType: "state",
                cityTitle: freshRoomInfo?.property?.state?.slug
              }
            },
            onClick: (event: MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              event.preventDefault();
              return router.push(
                {
                  pathname: "/[locationType]/[cityTitle]",
                  query: {
                    ...omit(router.query, "roomId"),
                    locationType: "state",
                    cityTitle: freshRoomInfo?.property?.state?.slug
                  }
                },
                undefined,
                { shallow: true, scroll: false }
              );
            }
          },
          {
            title: ` ویلاهای ${freshRoomInfo?.property?.city?.title}`,
            href: {
              pathname: "/[locationType]/[cityTitle]",
              query: {
                ...pick(router.query, ["cityTitle", "locationType"]),
                locationType: "city",
                cityTitle: freshRoomInfo?.property?.city?.slug
              }
            },
            onClick: (event: MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              event.preventDefault();
              return router.push(
                {
                  pathname: "/[locationType]/[cityTitle]",
                  query: {
                    ...omit(router.query, "roomId"),
                    locationType: "city",
                    cityTitle: freshRoomInfo?.property?.city?.slug
                  }
                },
                undefined,
                { shallow: true, scroll: false }
              );
            }
          },
          {
            title: `${freshRoomInfo?.property?.title}`,
            href: {
              pathname: "/room/[roomId]",
              query: pick(router.query, ["roomId"])
            }
          }
        ] as Array<BreadcrumbItemType>
      }) as BreadcrumbPropertiesType,
    [router, freshRoomInfo]
  );

  const peakDates = useMemo(
    () =>
      Object?.entries(calendarAvailabilityData?.calender || [])
        .filter((value) => value[1]?.dateType === "HighDemand")
        .map(([date]) => date),
    [calendarAvailabilityData?.calender]
  );
  const newYearDates = useMemo(
    () =>
      Object?.entries(calendarAvailabilityData?.calender || [])
        .filter((value) => value[1]?.dateType === "Nowruz")
        .map(([date]) => date),
    [calendarAvailabilityData?.calender]
  );

  const cancellationRuleHintText = useMemo(() => {
    const isSelectedDateInNewYearTime = newYearDates.some(
      (d) => d >= router.query.date_from && d <= router.query.date_to
    );
    if (isSelectedDateInNewYearTime) {
      return "مقرارت لغو رزرو در این تاریخ برابر با مقرارت ایام نوروز است.";
    }

    const isSelectedDateInPeakTime = peakDates.some(
      (d) => d >= router.query.date_from && d <= router.query.date_to
    );
    if (isSelectedDateInPeakTime) {
      return "مقرارت لغو رزرو در این تاریخ برابر با مقرارت پیک است.";
    }

    return "";
  }, [router.query.date_from, router.query.date_to, peakDates, newYearDates]);

  const policies = useMemo(
    () => [
      {
        type: CancellationRulesImportance.NORMAL,
        title: "روزهای عادی",
        text: freshRoomInfo?.property?.cancellation_rules_normal
      },
      {
        type: CancellationRulesImportance.PEAK_TIME,
        title: "روزهای پیک",
        text: freshRoomInfo?.property?.cancellation_rules_high_demand
      },
      {
        type: CancellationRulesImportance.NEW_YEAR,
        title: "ایام نوروز",
        text: freshRoomInfo?.property?.cancellation_rules_nowruz
      }
    ],
    [freshRoomInfo?.property]
  );
  const ceilTimeRange = (time) => {
    if (time.includes("-")) {
      const [start, end] = time.split("-");
      const ceilHour = (t) => {
        if (t === "infinite") return "infinite";
        const [h, m] = t.split(":").map(Number);
        return `${(h + (m > 0 ? 1 : 0)).toString().padStart(2, "0")}:00`;
      };

      return `${ceilHour(start)}-${ceilHour(end)}`;
    }

    const [h, m] = time.split(":").map(Number);
    return `${(h + (m > 0 ? 1 : 0)).toString().padStart(2, "0")}:00`;
  };

  return {
    calendarProps: {
      setQueryParameters,
      onCalendarChange,
      calendarDate,
      isLabelTop: true,
      calendarData: calendarAvailabilityData?.calender,
      flatCalendarIntitalShowDates: calendarInitialShowDates,
      disableDates,
      disableLimit
    } as HotelCalendarType,
    gallery: (freshRoomInfo?.gallery || [])?.map(({ title, name }, index) => ({
      id: index + 1,
      title,
      media: `${ENDPOINTS.CDN_IMAGES}/${name}`
    })),
    booking: {
      ...booking,
      cancellationRuleHintText,
      setQueryParameters,
      onChangeDatePicker: onCalendarChange,
      calendarDate,
      snappPayData,
      isInstant: roomProperty?.instant || false,
      isLoading: isFetching || requestBookingLoading,
      price:
        (roomProperty?.total_local_price_off || 0) -
        (roomProperty?.total_extra_bed_price || 0),
      minStay: roomProperty?.min_stay,
      passengersCount,
      capacity: freshRoomInfo?.capacity || 0,
      extraCapacity,
      priceOfExtraPassengers: roomProperty?.total_extra_bed_price || 0,
      pricePerNight: roomProperty?.from
        ? roomProperty?.daily_price?.[roomProperty?.from]?.sellPrice || 0
        : 0,
      total_price: roomProperty?.total_local_price_off || 0,
      basePrice: roomProperty?.base_price_off || 0,
      nightCount: daysBetweenDates(roomProperty?.from, roomProperty?.to) || 1,
      bookingCalendarIntitalShowDates: calendarInitialShowDates,
      increaseDisabled:
        passengersCount >=
        (roomProperty?.extra_bed > 0 ? freshRoomInfo?.extra_capacity : 0) +
          freshRoomInfo?.capacity,
      decreaseDisabled: passengersCount <= 1,
      submitDisabled:
        isPassengerLimitExceeded ||
        isEmpty(roomProperty) ||
        isEmpty(roomProperty.daily_price),
      submitDisabledMessage:
        isPassengerLimitExceeded ||
        (router?.query["date_from"] &&
          router?.query["date_to"] &&
          isEmpty(roomProperty))
          ? "امکان رزرو اقامتگاه در تاریخ و تعداد مسافر مشخص شده، وجود ندارد"
          : "",
      disableLimit: {
        disableAfterDate: lastDate
          ? transformDate(new Date(lastDate as unknown as string))
          : undefined
      },
      onChangePassengersCount,
      disableDates,
      calendarData: calendarAvailabilityData?.calender
    } as HotelReserveBookingCardType,
    hotelReserveCancellationPoliciesProps: {
      policies,
      cancellationPlanType:
        freshRoomInfo?.property?.cancellation_plan ||
        CancellationPlanType.UNKNOWN
    } as villaReserveCancellationPolicyType,
    hotelReservePoliciesProps: {
      checkinTime: numberConvert(
        ceilTimeRange(freshRoomInfo?.property?.check_in_time),
        {
          locale: router?.locale
        }
      ),
      checkoutTime: numberConvert(
        ceilTimeRange(freshRoomInfo?.property?.check_out_time),
        {
          locale: router?.locale
        }
      ),
      hotelType: "اقامتگاه",
      rulesAllowed: freshRoomInfo?.property?.rules_allowed,
      rulesDisAllowed: freshRoomInfo?.property?.rules_disallowed
    } as ReservePoliciesPropertiesTypes,
    hotelReserveHostPoliciesProps: {
      rules: freshRoomInfo?.property?.host_rule
    },
    hotelBreadcrumbsProps: hotelBreadcrumbsProperties,
    hotelReserveFacilitiesProps: {
      facilities: isEmpty(freshRoomInfo?.amenities)
        ? freshRoomInfo.property.amenities
        : freshRoomInfo?.amenities,
      facilitiesGroup: isEmpty(freshRoomInfo.amenitiesGroup)
        ? freshRoomInfo.property.amenitiesGroup
        : freshRoomInfo.amenitiesGroup,
      hotelTitle: freshRoomInfo?.title,
      hotelType: "اقامتگاه",
      onModalScrollChange: (value: number) =>
        handleFacilicyModalScrollChanged({ value: Number.parseInt(`${value}`) })
    } as ReserveFacilitiesPropertiesTypes,
    villaSimilarRoomsProps: {
      availability: roomProperty?.availability
    },
    hotelReserveDetailsProps: {
      id: freshRoomInfo.id,
      hotelTitle: seoData?.pageTitle || freshRoomInfo.title || "",
      address: [
        freshRoomInfo?.property?.state?.title,
        freshRoomInfo?.property?.city?.title,
        freshRoomInfo?.property?.village?.title
      ]
        ?.filter(Boolean)
        ?.join(", ")
    } as HotelReserveDetailsPropertiesTypes,
    villaSpecifications: [
      ...(freshRoomInfo?.summaries?.info
        ? [
            {
              iconName: "house" as MaterialNameType,
              title: "فضای اقامتگاه",
              description: numberConvert(freshRoomInfo?.summaries?.info, {
                locale: router?.locale
              })
            }
          ]
        : []),
      ...(freshRoomInfo?.capacity
        ? [
            {
              iconName: "group" as MaterialNameType,
              title: "ظرفیت",
              description: `${freshRoomInfo?.capacity} نفر پایه ${
                freshRoomInfo?.extra_capacity
                  ? `+ ${freshRoomInfo?.extra_capacity} نفر اضافه`
                  : ""
              }`
            },
            ...(roomProperty?.instant
              ? [
                  {
                    iconName: "bolt" as MaterialNameType,
                    title: "رزرو آنی",
                    description: "بدون نیاز به تایید میزبان"
                  }
                ]
              : []),
            ...(roomProperty?.extra_guest_base_price
              ? [
                  {
                    iconName: "person_add_alt_1" as MaterialNameType,
                    title: "هزینه نفر اضافه",
                    description: `${numberConvert(
                      numberSeparator(roomProperty?.extra_guest_base_price)
                    )} تومان`
                  }
                ]
              : []),
            ...(freshRoomInfo?.summaries?.bedding
              ? [
                  {
                    iconName: "bed" as MaterialNameType,
                    title: "سرویس خواب",
                    description: numberConvert(
                      freshRoomInfo?.summaries?.bedding,
                      {
                        locale: router?.locale
                      }
                    )
                  }
                ]
              : []),
            ...(freshRoomInfo?.summaries?.bathroom
              ? [
                  {
                    iconName: "shower" as MaterialNameType,
                    title: "سرویس بهداشتی",
                    description: numberConvert(
                      freshRoomInfo?.summaries?.bathroom,
                      {
                        locale: router?.locale
                      }
                    )
                  }
                ]
              : [])
          ]
        : [])
    ],
    villaSpaces: freshRoomInfo?.spaces?.map((space) => ({
      id: space?.id,
      title: space?.name,
      amenitiesGroup: Object?.entries(space?.amenitiesGroup)?.map((item) => ({
        title: item?.[0],
        items: item?.[1]?.map(({ title, description }) => ({
          title,
          description
        }))
      }))
    })),
    hotelReserveBtsCoveragesProps:
      freshRoomInfo?.property?.btsCoverage?.map((item) => ({
        ...item,
        ...(item?.cellOperator?.includes("ایرانسل")
          ? { logo: IrancellLogo?.src }
          : item?.cellOperator?.includes("اول")
            ? { logo: HamraheAval?.src }
            : item?.cellOperator?.includes("رایتل")
              ? { logo: Raitel?.src }
              : {})
      })) ?? [],
    hotelReserveRateProps: {
      ...hotelReserveRateProperties,
      pageTitle: seoData?.meta?.title || seoData?.meta?.fallbackTitle,
      pageDescription:
        seoData?.meta?.description ||
        seoData?.meta?.title ||
        seoData?.meta?.fallbackTitle
    },
    hotelReserveCommentsProps: {
      comments: comments.map((item) => ({
        ...item,
        helpfulSubmitted: !isLoggedIn || item?.helpfulSubmitted,
        submitSucceed:
          !isLoggedIn || reviewIds?.[reviewIds?.length - 1]?.id === item?.id
            ? isFetched
            : reviewIds?.filter(({ id }) => id === item?.id)?.[0]?.isFetched,
        onUsefulnessClick
      })),
      rate: hotelReserveRateProperties,
      hotelTitle: freshRoomInfo?.title,
      onLoadMore,
      isLoading,
      onLoadFilter,
      filters
    },
    hotelReserveMapProps:
      freshRoomInfo?.property?.location?.longitude &&
      freshRoomInfo?.property?.location?.latitude
        ? ({
            title: freshRoomInfo?.property?.title,
            type: "اقامتگاه",
            mapOptions: {
              center: {
                lng: freshRoomInfo?.property?.location?.longitude,
                lat: freshRoomInfo?.property?.location?.latitude
              }
            },
            circle: {
              lng: freshRoomInfo?.property?.location?.longitude,
              lat: freshRoomInfo?.property?.location?.latitude
            }
          } as ReserveMapPropertiesTypes)
        : null,
    hotelReserveNearByProps: freshRoomInfo?.property,
    description: freshRoomInfo?.property?.description,
    isAnyPolicyExist: policies.some((s) => Boolean(s?.text)),
    hotelSeoContentProps: {
      list: seoData?.seoContents ?? []
    } as HotelSeoContentPropertiesType,
    hotelSeoFAQProps: {
      list: seoData?.faq,
      title: "پاسخ به برخی از سوالات"
    } as HotelFaqPropertiesType,
    hotelInboundLinksProps: {
      list: seoData?.inboundLinks,
      title: "سفر بعدیت رو از الان برنامه ریزی کن"
    } as HotelInboundLinksPropertiesType
  };
};
